import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileDown } from 'lucide-react';
import heroBg from '../assets/auth/logistics-hero.png';
import '../styles/customs-guide-page.css';

const PDFS = [
  {
    title: 'Permissible & restricted articles',
    description: 'Official list of permissible and restricted articles (Feb 2026 PDF).',
    href: '/docs/permissible-articles-202602.pdf',
    fileLabel: 'Permissible-Articles-202602.pdf',
  },
  {
    title: 'Additional duty-free allowance (remittance)',
    description:
      'Passenger Baggage (exemption – Inwards) with additional duty-free allowance linked to foreign remittances to Sri Lanka — Regulation reference PDF.',
    href: '/docs/sl-additional-duty-free-allowance.pdf',
    fileLabel: 'sl-additional-duty-free-allowance.pdf',
  },
];

const ADULT_ALLOWANCE = [
  ['Less than 90 days', 'A', '187.5', '125'],
  ['More than 90 days but less than 365 days', 'B', '625', '500'],
  ['More than 365 days', 'C', '1,750', '1,000'],
];

const MINOR_ALLOWANCE = [
  ['Less than 90 days', 'D(I)', '125', '87.5'],
  ['More than 90 days', 'D(II)', '187.5', '187.5'],
];

const ADDITIONAL_ALLOWANCE = [
  ['Silver', '2,400 – 4,799', 'less than 90 days', '187.5', '600', '787.5'],
  ['Silver', '2,400 – 4,799', '90–365 days', '625', '600', '1,225'],
  ['Silver', '2,400 – 4,799', 'more than 365 days', '1,750', '600', '2,350'],
  ['Silver Plus', '4,800 – 7,199', 'less than 90 days', '187.5', '960', '1,147.5'],
  ['Silver Plus', '4,800 – 7,199', '90–365 days', '625', '960', '1,585'],
  ['Silver Plus', '4,800 – 7,199', 'more than 365 days', '1,750', '960', '2,710'],
  ['Gold', '7,200 – 11,999', 'less than 90 days', '187.5', '1,440', '1,627.5'],
  ['Gold', '7,200 – 11,999', '90–365 days', '625', '1,440', '2,065'],
  ['Gold', '7,200 – 11,999', 'more than 365 days', '1,750', '1,440', '3,190'],
  ['Gold Plus', '12,000 – 23,999', 'less than 90 days', '187.5', '2,400', '2,587.5'],
  ['Gold Plus', '12,000 – 23,999', '90–365 days', '625', '2,400', '3,025'],
  ['Gold Plus', '12,000 – 23,999', 'more than 365 days', '1,750', '2,400', '4,150'],
  ['Platinum', '24,000 or above', 'less than 90 days', '187.5', '4,800', '4,987.5'],
  ['Platinum', '12,000 – 23,999', '90–365 days', '625', '4,800', '5,425'],
  ['Platinum', '12,000 – 23,999', 'more than 365 days', '1,750', '4,800', '6,550'],
];

const SriLankaBaggageCustoms = () => {
  return (
    <div className="cg-page">
      <section className="cg-hero" aria-labelledby="cg-hero-title">
        <div className="cg-hero__bg" style={{ backgroundImage: `url(${heroBg})` }} />
        <div className="cg-hero__overlay" />
        <div className="cg-hero__inner">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="cg-hero__crumb">
              <Link to="/">Home</Link>
              <span aria-hidden> &gt; </span>
              Sri Lanka customs &amp; baggage
            </p>
            <h1 id="cg-hero-title" className="cg-hero__title">
              Sri Lanka passenger baggage &amp; customs guide
            </h1>
            <p className="cg-hero__lead">
              Summary of passenger types, baggage rules, exemptions, duty-free allowances (including UPB and remittance-linked
              benefits), declarations, and official contacts—compiled for Jet Shipping Cargo customers travelling to or from Sri
              Lanka.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="cg-shell">
        <div className="cg-disclaimer">
          <strong>Important:</strong> This page is for general customer guidance only. Allowances, categories, and procedures are
          determined by Sri Lanka Customs and may change. Always confirm with Customs and read the latest published regulations,
          gazettes, and notices. Jet Shipping Cargo is not a substitute for official advice.
        </div>

        <div id="pdfs" className="cg-downloads">
          {PDFS.map((doc) => (
            <div key={doc.href} className="cg-download-card">
              <h2>{doc.title}</h2>
              <p>{doc.description}</p>
              <a href={doc.href} download={doc.fileLabel} target="_blank" rel="noopener noreferrer">
                <FileDown size={18} aria-hidden />
                Download PDF
              </a>
            </div>
          ))}
        </div>

        <section id="passenger-types" className="cg-section" aria-labelledby="cg-passenger-heading">
          <h2 id="cg-passenger-heading">Passenger types</h2>
          <p>What type of passengers are there?</p>
          <h3>1. Resident</h3>
          <ul>
            <li>Sri Lankan passport holder (adults &amp; minors)</li>
            <li>Foreign passport holder with Sri Lanka resident visa</li>
          </ul>
          <h3>2. Crew</h3>
          <ul>
            <li>Airline crew (foreign nationals &amp; Sri Lankans)</li>
            <li>Ship&apos;s crew (foreign nationals &amp; Sri Lankans)</li>
          </ul>
          <h3>3. Tourist</h3>
          <h3>4. Transit</h3>
        </section>

        <section id="baggage-definition" className="cg-section">
          <h2>Baggage definition</h2>
          <p>What is passenger baggage?</p>
          <ul>
            <li>
              <strong>In the case of a passenger who is an ordinary resident in Sri Lanka</strong> — rules and concessions follow
              resident treatment as published by Customs.
            </li>
            <li>
              <strong>In the case of a passenger who is a tourist</strong> — rules and concessions follow tourist treatment as
              published by Customs.
            </li>
          </ul>
        </section>

        <section id="baggage-types" className="cg-section">
          <h2>Baggage types</h2>
          <p>What are the types of passenger baggage?</p>
          <ul>
            <li>Accompanied</li>
            <li>Mishandled</li>
            <li>Unaccompanied (air, sea, parcel post, favour parcels)</li>
          </ul>
        </section>

        <section id="exemptions" className="cg-section">
          <h2>Exemptions</h2>
          <p>Passenger baggage exemptions (summary).</p>

          <h3>Passenger&apos;s baggage exemptions — tourists</h3>
          <ul>
            <li>Personal clothing and other essentials</li>
            <li>Two regular-size bottles of wine and one and a half liters of spirits</li>
            <li>
              Toilet water not exceeding a quarter liter, and a small quantity of perfume and souvenirs, value not exceeding US
              $250, not meant for commercial purpose
            </li>
          </ul>

          <h3>Passenger&apos;s baggage exemptions — Sri Lankans / residents</h3>
          <ul>
            <li>The baggage of a passenger who is an ordinary resident in Sri Lanka</li>
            <li>
              Alcoholic liquor not exceeding two and a half liters, wine not exceeding two liters, and perfumed spirits not
              exceeding a quarter liter
            </li>
            <li>List of permissible and restricted articles (see downloadable PDF above)</li>
            <li>
              Passenger baggage of a Sri Lankan who has died on a voyage to Sri Lanka or of a citizen of Sri Lanka who has died
              abroad
            </li>
          </ul>

          <div className="cg-note">
            <strong>Note:</strong> Baggage concessions (free or otherwise) are granted only once within a period of one year (365
            days) unless otherwise determined by the Director General of Customs.
          </div>
        </section>

        <section id="allowance-adults" className="cg-section">
          <h2>How much is my duty-free allowance? — Adults</h2>
          <p>Exemption from Customs duty (USD).</p>
          <div className="cg-table-wrap">
            <table className="cg-table">
              <thead>
                <tr>
                  <th>Period of stay abroad</th>
                  <th>Category</th>
                  <th>Duty-free shop on arrival (USD)</th>
                  <th>UPB cargo (USD)</th>
                </tr>
              </thead>
              <tbody>
                {ADULT_ALLOWANCE.map(([period, cat, dfs, upb]) => (
                  <tr key={cat}>
                    <td>{period}</td>
                    <td>{cat}</td>
                    <td>{dfs}</td>
                    <td>{upb}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section id="allowance-minors" className="cg-section">
          <h2>Minors (under 18 years)</h2>
          <p>Exemption from Customs duty and other levies (USD).</p>
          <div className="cg-table-wrap">
            <table className="cg-table">
              <thead>
                <tr>
                  <th>Period of stay abroad</th>
                  <th>Category</th>
                  <th>Duty-free shop on arrival (USD)</th>
                  <th>UPB cargo (USD)</th>
                </tr>
              </thead>
              <tbody>
                {MINOR_ALLOWANCE.map(([period, cat, dfs, upb]) => (
                  <tr key={cat}>
                    <td>{period}</td>
                    <td>{cat}</td>
                    <td>{dfs}</td>
                    <td>{upb}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section id="allowance-upb" className="cg-section">
          <h2>UPB-related allowance notes</h2>
          <ul>
            <li>A passenger who has not utilized his/her baggage allowance on arrival is entitled to the baggage allowance for UPB.</li>
            <li>
              A passenger who has purchased goods at Sri Lanka duty-free shops and utilized the full allowance on arrival is not
              entitled to the baggage allowance for UPB.
            </li>
            <li>
              A passenger who has partially utilized the allowance is entitled to the proportionately calculated balance for UPB.
            </li>
          </ul>
        </section>

        <section id="allowance-additional" className="cg-section">
          <h2>Passenger baggage (exemption — inwards) with additional duty-free allowance</h2>
          <p>
            Linked to foreign remittances to Sri Lanka. Figures in USD. For full legal text refer to{' '}
            <strong>Regulation No. 01 of 2023</strong> and the downloadable PDF above.
          </p>
          <div className="cg-table-wrap">
            <table className="cg-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Min. remittance (USD)</th>
                  <th>Period abroad</th>
                  <th>Existing allowance (USD)</th>
                  <th>Additional (USD)</th>
                  <th>Total (USD)</th>
                </tr>
              </thead>
              <tbody>
                {ADDITIONAL_ALLOWANCE.map((row, i) => (
                  <tr key={`${row[0]}-${row[2]}-${i}`}>
                    {row.map((cell, j) => (
                      <td key={`${i}-${j}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section id="declaration" className="cg-section">
          <h2>Declaration</h2>
          <p>What should I declare to Customs?</p>
          <p>
            Foreign currencies, gold jewellery, goods under FRIC or CARNET, and gems should be declared to the Customs Arrival
            No. 1 officer.
          </p>
        </section>

        <section id="upb-clearance" className="cg-section">
          <h2>UPB clearance</h2>
          <p>How do I clear my UPB?</p>
          <p>
            Passengers should inquire from the relevant UPB warehouse or agent (for example Laksirisewa, TRICO, CSL, TRANSCO, ICD
            warehouse New Nuge Road, MIDCO, and others as applicable).
          </p>
        </section>

        <section id="duty-payment" className="cg-section">
          <h2>Duty payment</h2>
          <p>Do I have to pay duty?</p>
          <p>
            Goods of which value exceeds the passenger&apos;s allowance are taxed at the normal rates in the schedule of import
            duties; TT and excise duties are levied at appropriate rates where applicable.
          </p>
        </section>

        <section id="restricted" className="cg-section">
          <h2>Restricted / prohibited articles</h2>
          <p>What are the restricted / prohibited articles?</p>
          <p>
            Refer to <strong>Schedule B of the Customs Ordinance</strong> and the permissible / restricted articles PDF linked at
            the top of this page.
          </p>
        </section>

        <section id="breach" className="cg-section">
          <h2>Breach of law</h2>
          <p>What happens if I breach the law?</p>
          <p>
            If any passenger knowingly or wilfully makes a declaration which is false or inaccurate, he or she shall not be entitled
            to any exceptions under the regulations and shall be liable to be dealt with under the provisions of the Customs
            Ordinance.
          </p>
        </section>

        <section id="contact" className="cg-section">
          <h2>Contact</h2>
          <p>Whom should I contact in case of any doubt?</p>
          <div className="cg-contact-box">
            <strong>Customs Information Desk</strong>
            <span>Customs House, Colombo</span>
            <br />
            <a href="tel:+94112221333">+94 11 222 1333</a>
          </div>
        </section>

        <div className="cg-downloads" style={{ marginTop: '0.5rem' }}>
          {PDFS.map((doc) => (
            <div key={`${doc.href}-foot`} className="cg-download-card">
              <h2>{doc.title}</h2>
              <p>Download again if needed.</p>
              <a href={doc.href} download={doc.fileLabel} target="_blank" rel="noopener noreferrer">
                <FileDown size={18} aria-hidden />
                Download PDF
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SriLankaBaggageCustoms;
